"use client";

import React, { useEffect, useState } from "react";
import { ChevronRight, FileCheck } from "lucide-react";
import { SectionHeading } from "@/components/cosmic/SectionHeading";
import { scrollToElement } from "@/lib/scroll";
interface PolicySectionItem {
  id: string;
  badge: string;
  title: string;
  paragraphs: React.ReactNode[];
}
const REFUND_SECTIONS: PolicySectionItem[] = [{
  id: "digital-products-non-refundable",
  badge: "01 / DIGITAL PRODUCTS",
  title: "All Digital Products Are Non-Refundable",
  paragraphs: [<p key="p1">
        At Quantum Liaison, we offer digital products, downloadable content, personalized digital dossiers, interactive experiences, and other digital goods.
      </p>, <p key="p2">
        <strong>All purchases of digital products are final and non-refundable</strong>, except where a refund, cancellation, or other remedy is required by applicable law.
      </p>, <p key="p3">
        Because digital products may be delivered electronically, accessed immediately, or generated specifically for the customer, we generally cannot accept returns or provide refunds once an order has been completed.
      </p>]
}, {
  id: "personalized-and-custom-products",
  badge: "02 / PERSONALIZED PRODUCTS",
  title: "Personalized and Custom-Generated Products",
  paragraphs: [<p key="p1">
        Certain Quantum Liaison products, including personalized dossiers and reports, are created using the information and preferences submitted by the customer.
      </p>, <p key="p2">
        Once a personalized product has been generated, delivered, or made available through a purchase link, the purchase is non-refundable, except where required by applicable law or where a refund is otherwise approved under this policy.
      </p>, <p key="p3">
        Customers are responsible for reviewing their submitted information before completing their purchase.
      </p>]
}, {
  id: "downloads-and-digital-delivery",
  badge: "03 / DIGITAL DELIVERY",
  title: "Downloads and Digital Delivery",
  paragraphs: [<p key="p1">
        Customers are responsible for providing an accurate email address and ensuring they can access the email account, device, software, and internet connection needed to receive or download their purchases.
      </p>, <p key="p2">
        If you do not receive your digital product, experience a broken download link, or encounter a technical problem accessing your purchase, please contact us. We will make reasonable efforts to help you access the product you purchased.
      </p>, <p key="p3">
        A delivery problem does not automatically entitle a customer to a refund, but we will assess the issue fairly and provide any remedy required by applicable law.
      </p>]
}, {
  id: "duplicate-purchases",
  badge: "04 / DUPLICATE PURCHASES",
  title: "Duplicate Purchases",
  paragraphs: [<p key="p1">
        If you believe you have accidentally purchased the same digital product more than once, contact us with the relevant order details. We will review the circumstances and determine an appropriate resolution, subject to applicable law.
      </p>]
}, {
  id: "incorrect-or-defective-products",
  badge: "05 / DEFECTIVE PRODUCTS",
  title: "Incorrect or Defective Digital Products",
  paragraphs: [<p key="p1">
        If a digital product is corrupted, materially different from its description, or does not function as advertised, please contact us so we can investigate and, where appropriate, provide a corrected product, replacement, or other legally required remedy.
      </p>, <p key="p2">
        Nothing in this policy excludes or limits any consumer rights that cannot legally be excluded or limited.
      </p>]
}, {
  id: "chargebacks-and-payment-disputes",
  badge: "06 / PAYMENT DISPUTES",
  title: "Chargebacks and Payment Disputes",
  paragraphs: [<p key="p1">
        If you have a problem with a purchase, we encourage you to contact us first so we can attempt to resolve the issue.
      </p>, <p key="p2">
        This does not prevent you from exercising any applicable rights through your payment provider or under applicable law.
      </p>]
}, {
  id: "how-to-contact-us",
  badge: "07 / SUPPORT CONTACT",
  title: "How to Contact Us",
  paragraphs: [<p key="p1">
        For questions about refunds, digital delivery, or access to a purchase, contact:
      </p>, <p key="p2">
        <strong>Quantum Liaison</strong>
      </p>, <p key="p3">
        Email:{" "}
        <a href="mailto:support@quantum-liaison.com" className="text-primary underline underline-offset-4 hover:brightness-110 transition-all font-medium inline-block min-h-[44px] py-2 sm:py-0 sm:inline">
          support@quantum-liaison.com
        </a>
      </p>, <p key="p4">
        Please include your order number, the email address used for your purchase, and a brief description of the issue. Do not send payment-card details or passwords.
      </p>]
}, {
  id: "policy-updates",
  badge: "08 / REVISIONS",
  title: "Policy Updates",
  paragraphs: [<p key="p1">
        Quantum Liaison may update this Refund Policy from time to time. Any changes will be published on this page with an updated revision date.
      </p>, <p key="p2">
        By purchasing a digital product, you acknowledge that you have read this policy, subject to your rights under applicable law.
      </p>]
}];
export default function RefundPolicy() {
  const [activeId, setActiveId] = useState<string>(REFUND_SECTIONS[0].id);
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 160;
      for (let i = REFUND_SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(REFUND_SECTIONS[i].id);
        if (el && el.offsetTop <= scrollPosition) {
          setActiveId(REFUND_SECTIONS[i].id);
          break;
        }
      }
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section id="refund-policy" data-nav="dark" className="relative text-foreground py-12 md:py-20 lg:py-24 w-full max-w-full">
      <div className="relative mx-auto max-w-6xl w-full px-3.5 sm:px-6 lg:px-8">
        {/* Compact Hero with SectionHeading */}
        <div className="mb-8 sm:mb-16 text-center">
          <SectionHeading as="h1" eyebrow="Legal" title="Refund Policy" align="center" />
          <p className="mt-3 font-mono text-xs sm:text-sm text-muted-foreground">
            Last Updated: October 17, 2026
          </p>
        </div>

        {/* Monolith layout with sticky desktop Table of Contents */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 sm:gap-8 lg:gap-12 items-start w-full">
          {/* Sticky Table of Contents Sidebar */}
          <aside className="w-full min-w-0 lg:col-span-4 lg:sticky lg:top-28">
            <div className="ql-glass p-4 sm:p-6 min-w-0 break-words">
              <div className="flex items-center justify-between pb-3 border-b border-border/60 mb-3 sm:mb-4">
                <span className="font-mono text-xs uppercase tracking-widest text-primary font-semibold flex items-center gap-2">
                  <span className="size-1.5 rotate-45 bg-primary" aria-hidden="true" />
                  Table of Contents
                </span>
                <span className="font-mono text-[11px] text-muted-foreground">
                  {REFUND_SECTIONS.length} Sections
                </span>
              </div>

              {/* Mobile Quick-Jump Chips (buttons with scrollToElement, 44px min tap target) */}
              <nav aria-label="Refund policy sections (mobile)" className="flex lg:hidden flex-wrap gap-2 mb-2 w-full">
                {REFUND_SECTIONS.map((section, idx) => {
                const isActive = activeId === section.id;
                return <button key={section.id} type="button" onClick={() => scrollToElement(section.id)} className={`ql-chip min-h-[44px] cursor-pointer text-left ${isActive ? "data-[active=true]" : ""}`} data-active={isActive ? "true" : "false"}>
                      <span className="text-[10px] opacity-75 shrink-0">0{idx + 1}</span>
                      <span className="break-words">{section.title}</span>
                    </button>;
              })}
              </nav>

              {/* Desktop links list (buttons with scrollToElement) */}
              <nav aria-label="Refund policy sections (desktop)" className="hidden lg:flex flex-col gap-2">
                {REFUND_SECTIONS.map((section, idx) => {
                const isActive = activeId === section.id;
                return <button key={section.id} type="button" onClick={() => scrollToElement(section.id)} className={`group flex items-center justify-between p-3 rounded-lg border text-left transition-all min-h-[44px] cursor-pointer w-full ${isActive ? "border-primary/60 bg-primary/10 text-foreground shadow-[0_0_15px_-3px_var(--glow-primary)]" : "border-transparent hover:border-border hover:bg-card/60 text-muted-foreground hover:text-foreground"}`}>
                      <div className="flex items-center gap-3 min-w-0">
                        <span className={`font-mono text-xs px-2 py-0.5 rounded border transition-colors shrink-0 ${isActive ? "border-primary/60 bg-primary/20 text-primary font-semibold" : "border-border/60 bg-muted/40 text-muted-foreground group-hover:text-foreground"}`}>
                          0{idx + 1}
                        </span>
                        <span className="text-sm font-medium truncate font-sans">
                          {section.title}
                        </span>
                      </div>
                      <ChevronRight className={`size-4 shrink-0 transition-transform ${isActive ? "text-primary translate-x-0.5" : "text-muted-foreground/40 group-hover:text-foreground group-hover:translate-x-0.5"}`} />
                    </button>;
              })}
              </nav>
            </div>
          </aside>

          {/* Reading Column: ql-glass document panel (~72ch readable width) */}
          <div className="w-full min-w-0 lg:col-span-8 flex flex-col gap-6">
            <div className="ql-glass p-5 sm:p-10 md:p-12 min-w-0 break-words">
              {/* Sections List */}
              <div className="flex flex-col gap-6 sm:gap-8">
                {REFUND_SECTIONS.map((item, index) => {
                const isActive = activeId === item.id;
                return <article key={item.id} id={item.id} className={`scroll-mt-28 transition-colors p-4 sm:p-6 rounded-xl border min-w-0 break-words ${isActive ? "border-primary/40 bg-card/80 shadow-[0_4px_24px_-4px_rgba(0,0,0,0.5)]" : "border-border/60 bg-card/40"}`}>
                      <div className="flex items-start sm:items-center justify-between gap-3 sm:gap-4 mb-4 flex-wrap">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="flex size-9 sm:size-10 shrink-0 items-center justify-center rounded-lg border border-primary/40 bg-primary/10 text-primary shadow-[0_0_12px_-2px_var(--glow-primary)]">
                            <FileCheck className="size-4 sm:size-5" />
                          </div>
                          <div className="min-w-0">
                            <span className="block font-mono text-[11px] font-semibold text-primary tracking-widest uppercase">
                              {item.badge}
                            </span>
                            <h2 className="font-serif text-base sm:text-xl font-bold text-foreground tracking-tight break-words">
                              {index + 1}. {item.title}
                            </h2>
                          </div>
                        </div>
                      </div>

                      <div className="text-xs sm:text-base text-muted-foreground leading-relaxed pl-0 sm:pl-13 break-words space-y-3">
                        {item.paragraphs}
                      </div>
                    </article>;
              })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}